import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  forwardRef,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import {
  clampNumber,
  isNumericText,
  NumberBounds,
  parseNumericText,
  toNumberValue,
  toPlainNumberText,
} from 'src/app/core/utils/number-utils';

/**
 * A single number, typed.
 *
 * Two things it does that are worth reading the code for:
 *
 * **An edit that is not a number is refused whole** and the field goes back to the text it had,
 * caret included. `number-utils` argues that out; the short version is that every alternative loses
 * digits the reader can no longer see.
 *
 * **Nothing is parsed until it is committed.** `-`, `.` and `-.` are all states on the way to a
 * number, so the field holds them, and it is blur or Enter that turns the text into a value.
 * Filtering them out on every keystroke is what used to make a negative number impossible to type
 * and `.5` save as `5`.
 *
 * A value outside `min`/`max` is clamped quietly on commit, with the corrected number appearing in
 * the field as the only feedback — the same as the other two implementations of this field.
 */
@Component({
  selector: 'craft-number-input',
  templateUrl: './craft-number-input.component.html',
  styleUrls: ['./craft-number-input.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => CraftNumberInputComponent),
      multi: true,
    },
  ],
  standalone: false,
})
export class CraftNumberInputComponent implements ControlValueAccessor, OnChanges, AfterViewInit {
  @Output() public update = new EventEmitter<number | null>();

  /*
   * Typed loosely because a bound really does arrive as a string: `CustomField.min`/`max` are
   * declared `number`, but only `portal-backend.service` coerces them — the two loaders in
   * `custom-fields-backend.service` pass the server's strings straight through, and those are the
   * ones behind the feedback form and the company screen. They are read through `toNumberValue`
   * below rather than trusted, which is also what drops an empty string back to "no bound".
   */
  @Input() public min?: number | string | null;
  @Input() public max?: number | string | null;
  @Input() public allowNull = false;
  @Input() public showButtons = false;
  @Input() public isDisabled = false;
  @Input() public placeholder = 'Set';
  @Input() public value?: string | number | null;

  @ViewChild('input') private inputRef?: ElementRef<HTMLInputElement>;

  public onTouched = () => {};
  public onChange = (_?: number | null) => {};

  /*
   * The last text the filter accepted. A refused edit has to be undone, and by then the field
   * already holds the refused text, so there is nowhere else to read the previous state from — the
   * event reports what happened, not what was about to.
   */
  public val = '';

  private committed: number | null = null;

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.value) {
      this.showNumber(toNumberValue(changes.value.currentValue));
    }
  }

  /**
   * The field's own text is written here rather than bound in the template.
   *
   * The refusal below writes the node directly, and a template binding would fight it: it only
   * touches the node when the two differ, so the value it holds and the value Angular thinks it
   * holds would drift apart on exactly the path that matters.
   */
  public ngAfterViewInit(): void {
    this.showText(this.val);
  }

  public writeValue(val: unknown): void {
    this.showNumber(toNumberValue(val as string | number | null | undefined));
  }

  public registerOnChange(fn: (_?: number | null) => void): void {
    this.onChange = fn;
  }

  public registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  public setDisabledState(isDisabled: boolean): void {
    this.isDisabled = isDisabled;
  }

  public handleInput(): void {
    const input = this.inputRef?.nativeElement;
    if (!input) return;

    if (isNumericText(input.value)) {
      this.val = input.value;
      return;
    }

    this.refuseEdit(input);
  }

  public handleBlur(): void {
    this.commit();
    this.onTouched();
  }

  public handleEnter(): void {
    this.commit();
  }

  public handleEscape(): void {
    this.showNumber(this.committed);
  }

  public plus(): void {
    this.step(1);
  }

  public minus(): void {
    this.step(-1);
  }

  /** Whether a step in this direction would move the number — what greys the button out. */
  public canStep(by: number): boolean {
    const from = this.numberInField();

    return clampNumber(from + by, this.bounds()) !== from;
  }

  /**
   * Turns whatever is in the field into the value, and shows what that turned out to be.
   *
   * Runs on blur and on Enter, never on a keystroke: clamping mid-word would fight the reader for
   * the field, and `12` on the way to `120` is below a `min` of `100` for as long as it takes to
   * reach for the next key.
   */
  private commit(): void {
    const parsed = parseNumericText(this.val);
    const next = parsed === null ? this.emptyValue : clampNumber(parsed, this.bounds());

    this.commitNumber(next);
  }

  private step(by: number): void {
    this.commitNumber(clampNumber(this.numberInField() + by, this.bounds()));
  }

  private commitNumber(next: number | null): void {
    const hasChanged = next !== this.committed;

    // Unconditional, and the only feedback a corrected entry gets: `007` becomes `7`, `1.` becomes
    // `1`, `-` empties the field and `999` becomes the maximum, all as the field loses the focus.
    // Conditioning it on `hasChanged` would leave `007` on screen next to a value of `7`.
    this.showNumber(next);

    if (hasChanged) {
      this.onChange(next);
      this.update.next(next);
    }
  }

  private showNumber(value: number | null): void {
    this.committed = value;
    this.showText(toPlainNumberText(value));
  }

  private showText(text: string): void {
    this.val = text;

    const input = this.inputRef?.nativeElement;
    if (input) {
      input.value = text;
    }
  }

  /**
   * Puts the field back the way it was, caret and all.
   *
   * Nothing changes on screen, which is the point: the reader sees their keystroke or their paste
   * simply not take, rather than seeing the number they had turn into a shorter one.
   */
  private refuseEdit(input: HTMLInputElement): void {
    /*
     * Where the caret was before the edit. An insertion of `k` characters at `p` leaves the caret at
     * `p + k` and the text `k` longer, so taking the growth back off lands on `p`. When the edit
     * replaced a selection the same sum lands at the end of what was selected, which is as close to
     * unchanged as one number can be.
     *
     * The bounds are not paranoia: `setSelectionRange` takes an `unsigned long`, so a negative
     * offset is not clamped to 0 but converted to an enormous positive one, and the caret jumps to
     * the far side of the number instead of staying put.
     */
    const growth = input.value.length - this.val.length;
    const caret = (input.selectionStart ?? this.val.length) - growth;
    const bounded = Math.min(Math.max(caret, 0), this.val.length);

    input.value = this.val;
    input.setSelectionRange(bounded, bounded);
  }

  /** The number the field is showing, for a step to start from. */
  private numberInField(): number {
    return parseNumericText(this.val) ?? this.emptyValue ?? 0;
  }

  private get emptyValue(): number | null {
    return this.allowNull ? null : (toNumberValue(this.min) ?? 0);
  }

  private bounds(): NumberBounds {
    return { min: toNumberValue(this.min), max: toNumberValue(this.max) };
  }
}
